import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { errorMessage } from '@/api/http'
import { auth } from '@/lib/auth'
import { Button, Card, ErrorBox, Field, Input } from '@/components/ui'

export const Route = createFileRoute('/_auth/login')({
  /**
   * `redirect` v query drzi cestu, na kterou uzivatel puvodne miril, nez ho
   * `_app` poslal sem. Validace je zaroven typovanim: komponenta pak dostane
   * `search.redirect` jako `string | undefined`, ne `any`.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()

  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [customerName, setCustomerName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (mode === 'login') {
        await auth.login(email, password)
      } else {
        await auth.register(customerName, email, password)
      }
      // Tvrdy prechod pres href je tu schvalne: po prihlaseni se meni kontext
      // routeru (auth), a nacteni cile nacisto je jistejsi nez ho invalidovat.
      navigate({ to: redirect ?? '/dashboard', replace: true })
    } catch (err) {
      setError(errorMessage(err, mode === 'login' ? 'Prihlaseni se nezdarilo' : 'Registrace se nezdarila'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {(['login', 'register'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m)
                setError(null)
              }}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {m === 'login' ? 'Prihlaseni' : 'Nova organizace'}
            </button>
          ))}
        </div>

        {mode === 'register' && (
          <Field label="Organizace">
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="INDIVIDUAL STARTUP s.r.o."
              required
              autoComplete="organization"
            />
          </Field>
        )}

        <Field label="E-mail">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jmeno@firma.cz"
            required
            autoComplete="username"
          />
        </Field>

        <Field label="Heslo" hint={mode === 'register' ? 'Aspon 10 znaku' : undefined}>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </Field>

        <ErrorBox message={error} />

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Odesilam…' : mode === 'login' ? 'Prihlasit' : 'Zalozit organizaci'}
        </Button>
      </form>
    </Card>
  )
}
